import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Progress } from "../components/ui/progress";
import { ArrowLeft, ArrowRight, CheckCircle, XCircle, Loader2, Download, BookOpen } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { API, useAuth, getAuthHeaders } from "../App";

const QuizTake = () => {
  const { quizId } = useParams();
  const navigate = useNavigate();
  const { token, refreshUser, user } = useAuth();
  
  const [quiz, setQuiz] = useState(null);
  const [attempt, setAttempt] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [answerResult, setAnswerResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [finalResult, setFinalResult] = useState(null);

  useEffect(() => {
    startQuiz();
  }, [quizId]);

  const startQuiz = async () => {
    try {
      const response = await axios.post(`${API}/quizzes/${quizId}/start`, {}, {
        headers: getAuthHeaders(token)
      });
      setQuiz(response.data.quiz);
      setAttempt(response.data);
    } catch (error) {
      toast.error("Failed to start quiz");
      navigate("/quizzes");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAnswer = (answer) => {
    if (!showResult) {
      setSelectedAnswer(answer);
    }
  };

  const handleSubmitAnswer = async () => {
    if (!selectedAnswer) {
      toast.error("Please select an answer");
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API}/quizzes/attempts/${attempt.attempt_id}/answer`,
        {
          question_id: quiz.questions[currentIndex].question_id,
          answer: selectedAnswer
        },
        { headers: getAuthHeaders(token) }
      );
      
      setAnswerResult(response.data);
      setShowResult(true);
    } catch (error) {
      toast.error("Failed to submit answer");
    } finally {
      setSubmitting(false);
    }
  };

  const handleNext = async () => {
    if (currentIndex < quiz.questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setSelectedAnswer(null);
      setShowResult(false);
      setAnswerResult(null);
    } else {
      // Complete quiz
      try {
        const response = await axios.post(
          `${API}/quizzes/attempts/${attempt.attempt_id}/complete`,
          {},
          { headers: getAuthHeaders(token) }
        );
        setFinalResult(response.data);
        setCompleted(true);
      } catch (error) {
        toast.error("Failed to complete quiz");
      }
    }
  };

  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    if ((user?.credits || 0) < 2) {
      toast.error("Insufficient credits for download (2 credits needed)");
      return;
    }

    setDownloading(true);
    try {
      const response = await axios.post(
        `${API}/quizzes/attempts/${attempt.attempt_id}/download`,
        {},
        { 
          headers: getAuthHeaders(token),
          responseType: 'blob'
        }
      );
      
      await refreshUser();
      
      // Get filename from Content-Disposition header or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = `quiz-results-${quizId}.pdf`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^";\n]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link for PDF blob
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      
      toast.success("Quiz results downloaded!");
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error("Insufficient credits");
      } else {
        toast.error("Failed to download quiz results");
      }
    } finally {
      setDownloading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-white" />
      </div>
    );
  }

  if (completed) {
    const percentage = Math.round((finalResult.score / finalResult.total) * 100);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4" data-testid="quiz-complete">
        <Card className="max-w-lg w-full bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="p-8 text-center">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6 ${
              percentage >= 70 ? 'bg-green-500' : percentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
            }`}>
              {percentage >= 70 ? (
                <CheckCircle className="w-10 h-10 text-white" />
              ) : (
                <BookOpen className="w-10 h-10 text-white" />
              )}
            </div>
            
            <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Quiz Complete!
            </h1>
            
            <p className="text-6xl font-bold text-white mb-2">{percentage}%</p>
            <p className="text-white/60 mb-6">
              {finalResult.score} out of {finalResult.total} correct
            </p>
            
            <p className="text-white/80 mb-8">
              {percentage >= 70 
                ? "Great job! You have a solid understanding of this material."
                : percentage >= 50
                ? "Good effort! Review the explanations to strengthen your knowledge."
                : "Keep learning! Try reviewing the content and taking the quiz again."}
            </p>
            
            <div className="flex gap-4">
              <Button
                onClick={() => navigate("/quizzes")}
                variant="outline"
                className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                data-testid="back-to-quizzes-btn"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Quizzes
              </Button>
              <Button
                onClick={handleDownload}
                disabled={downloading}
                className="flex-1 bg-white text-slate-900 hover:bg-slate-100"
                data-testid="download-results-btn"
              >
                {downloading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download (2 credits)
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const currentQuestion = quiz?.questions[currentIndex];
  const progress = ((currentIndex + 1) / quiz?.questions.length) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 p-4" data-testid="quiz-take-page">
      {/* Header */}
      <div className="max-w-3xl mx-auto mb-8">
        <div className="flex items-center justify-between text-white/60 mb-4">
          <Button
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/10"
            onClick={() => navigate("/quizzes")}
            data-testid="exit-quiz-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Exit Quiz
          </Button>
          <span>
            Question {currentIndex + 1} of {quiz?.questions.length}
          </span>
        </div>
        <Progress value={progress} className="h-2 bg-white/10" />
      </div>

      {/* Question Card */}
      <div className="max-w-3xl mx-auto">
        <Card className="bg-white/10 backdrop-blur-xl border-white/20">
          <CardContent className="p-8">
            {/* Question */}
            <div className="mb-8">
              <span className="inline-block px-3 py-1 bg-white/10 rounded-full text-sm text-white/60 mb-4">
                {currentQuestion?.type === 'multiple_choice' ? 'Multiple Choice' : 'True/False'}
              </span>
              <h2 className="text-2xl font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {currentQuestion?.question}
              </h2>
            </div>

            {/* Options */}
            <div className="space-y-3 mb-8">
              {currentQuestion?.type === 'multiple_choice' ? (
                currentQuestion?.options?.map((option, i) => {
                  const isCorrectOption = showResult && option.toLowerCase().trim() === answerResult?.correct_answer?.toLowerCase().trim();
                  const isSelectedWrong = showResult && option === selectedAnswer && !answerResult?.is_correct;
                  
                  return (
                    <button
                      key={i}
                      onClick={() => handleSelectAnswer(option)}
                      disabled={showResult}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        showResult
                          ? isCorrectOption
                            ? 'bg-green-500/20 border-2 border-green-500 text-white'
                            : isSelectedWrong
                            ? 'bg-red-500/20 border-2 border-red-500 text-white'
                            : 'bg-white/5 border-2 border-white/10 text-white/60'
                          : selectedAnswer === option
                          ? 'bg-white/20 border-2 border-white text-white'
                          : 'bg-white/5 border-2 border-white/10 text-white hover:bg-white/10'
                      }`}
                      data-testid={`option-${i}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{option}</span>
                        {showResult && isCorrectOption && (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        )}
                        {showResult && isSelectedWrong && (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                    </button>
                  );
                })
              ) : (
                ['True', 'False'].map((option, i) => {
                  const isCorrectOption = showResult && option.toLowerCase() === answerResult?.correct_answer?.toLowerCase();
                  const isSelectedWrong = showResult && option === selectedAnswer && !answerResult?.is_correct;
                  
                  return (
                    <button
                      key={i}
                      onClick={() => handleSelectAnswer(option)}
                      disabled={showResult}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        showResult
                          ? isCorrectOption
                            ? 'bg-green-500/20 border-2 border-green-500 text-white'
                            : isSelectedWrong
                            ? 'bg-red-500/20 border-2 border-red-500 text-white'
                            : 'bg-white/5 border-2 border-white/10 text-white/60'
                          : selectedAnswer === option
                          ? 'bg-white/20 border-2 border-white text-white'
                          : 'bg-white/5 border-2 border-white/10 text-white hover:bg-white/10'
                      }`}
                      data-testid={`option-${option.toLowerCase()}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium">{option}</span>
                        {showResult && isCorrectOption && (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        )}
                        {showResult && isSelectedWrong && (
                          <XCircle className="w-5 h-5 text-red-400" />
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Result Explanation */}
            {showResult && answerResult && (
              <div className={`p-4 rounded-xl mb-8 ${
                answerResult.is_correct ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'
              }`}>
                <div className="flex items-center gap-2 mb-2">
                  {answerResult.is_correct ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-400" />
                  )}
                  <span className={`font-semibold ${answerResult.is_correct ? 'text-green-400' : 'text-red-400'}`}>
                    {answerResult.is_correct ? 'Correct!' : 'Incorrect'}
                  </span>
                </div>
                <p className="text-white/80 text-sm">{answerResult.explanation}</p>
              </div>
            )}

            {/* Action Button */}
            {!showResult ? (
              <Button
                onClick={handleSubmitAnswer}
                disabled={!selectedAnswer || submitting}
                className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100"
                data-testid="submit-answer-btn"
              >
                {submitting ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  'Submit Answer'
                )}
              </Button>
            ) : (
              <Button
                onClick={handleNext}
                className="w-full h-12 bg-white text-slate-900 hover:bg-slate-100"
                data-testid="next-question-btn"
              >
                {currentIndex < quiz.questions.length - 1 ? (
                  <>
                    Next Question
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                ) : (
                  'See Results'
                )}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default QuizTake;
